import {
  IIndirectTaxFilingV2AggregateRegion,
  IIndirectTaxFilingV2AggregateResponse,
  IIndirectTaxFilingV2AggregateTotals,
  IIndirectTaxFilingV2DocumentWithFile,
  IIndirectTaxFilingV2ServiceRequestAssociation,
  IIndirectTaxFilingV2WithAssociations,
} from '@commenda/api/indirect-tax/filings/types';
import { LegacyDocumentType, SalesTaxFilingStatusEnum } from '@prisma/client';

import { FilterTab } from '../../../components';
import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import {
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionTableFilters,
} from '../../transactions/types';

import {
  IIndirectTaxFiling,
  IIndirectTaxFilingDetail,
  IIndirectTaxFilingDocument,
  IIndirectTaxFilingReturnSummary,
  IIndirectTaxFilingStatusBadgeConfig,
  IIndirectTaxFilingStatusStyles,
  IIndirectTaxFilingSummaryConfig,
  IIndirectTaxFilingTask,
  IndirectTaxFilingClientActionType,
  IndirectTaxFilingStatus,
  IndirectTaxFilingTaskStatus,
  IndirectTaxJurisdictionType,
} from '../types';

import { IndirectTaxFilingListRowHelper } from './IndirectTaxFilingListRowHelper';

export class IndirectTaxFilingsHelper {
  static readonly STATUS_BADGE_CONFIG: Record<
    IndirectTaxFilingStatus,
    IIndirectTaxFilingStatusBadgeConfig
  > = {
    [IndirectTaxFilingStatus.ACTIVE]: { label: 'Active', variant: 'blue' },
    [IndirectTaxFilingStatus.FILED]: { label: 'Filed', variant: 'success' },
    [IndirectTaxFilingStatus.SCHEDULED]: {
      label: 'Scheduled',
      variant: 'warning',
    },
    [IndirectTaxFilingStatus.ACCRUING]: { label: 'Accruing', variant: 'gray' },
    [IndirectTaxFilingStatus.OVERDUE]: { label: 'Overdue', variant: 'error' },
  };

  static readonly STATUS_TAB_ORDER: IndirectTaxFilingStatus[] = [
    IndirectTaxFilingStatus.ACTIVE,
    IndirectTaxFilingStatus.ACCRUING,
    IndirectTaxFilingStatus.OVERDUE,
    IndirectTaxFilingStatus.FILED,
    IndirectTaxFilingStatus.SCHEDULED,
  ];

  static readonly SUMMARY_CONFIGS: Record<
    IndirectTaxRegion,
    IIndirectTaxFilingSummaryConfig[]
  > = {
    [IndirectTaxRegion.ASIA_PACIFIC]: [],
    [IndirectTaxRegion.MIDDLE_EAST]: [],
    [IndirectTaxRegion.LATIN_AMERICA]: [],
    [IndirectTaxRegion.UNSUPPORTED]: [],
    [IndirectTaxRegion.ALL_REGIONS]: [
      { label: 'Total sales', key: 'totalSales', isCurrency: true },
      { label: 'Taxable sales', key: 'taxableSales', isCurrency: true },
      { label: 'Tax due', key: 'taxDue', isCurrency: true },
    ],
    [IndirectTaxRegion.UNITED_STATES]: [
      { label: 'Taxable sales', key: 'taxableSales', isCurrency: true },
      { label: 'Non-taxable sales', key: 'nonTaxableSales', isCurrency: true },
      { label: 'Exempt sales', key: 'exemptSales', isCurrency: true },
      { label: 'Tax due', key: 'taxDue', isCurrency: true },
      { label: 'Total sales', key: 'totalSales', isCurrency: true },
    ],
    [IndirectTaxRegion.EUROPEAN_UNION]: [
      { label: 'Taxable sales', key: 'taxableSales', isCurrency: true },
      { label: 'Taxable purchases', key: 'taxablePurchases', isCurrency: true },
      {
        label: 'Intra-community sales',
        key: 'intraCommunitySales',
        isCurrency: true,
      },
      {
        label: 'Intra-community purchases',
        key: 'intraCommunityPurchases',
        isCurrency: true,
      },
      { label: 'VAT position', key: 'vatPosition', isCurrency: true },
    ],
    [IndirectTaxRegion.UNITED_KINGDOM]: [
      { label: 'Taxable sales', key: 'taxableSales', isCurrency: true },
      { label: 'Taxable purchases', key: 'taxablePurchases', isCurrency: true },
      { label: 'VAT position', key: 'vatPosition', isCurrency: true },
    ],
    // Canada card set. Only consulted when aggregate cards are shown (see
    // supportsAggregate); exemptSales / totalSales have no contribution
    // source, so they render as '—' once Canada is contribution-driven.
    [IndirectTaxRegion.CANADA]: [
      { label: 'Taxable sales', key: 'taxableSales', isCurrency: true },
      { label: 'Exempt sales', key: 'exemptSales', isCurrency: true },
      { label: 'Tax due', key: 'taxDue', isCurrency: true },
      { label: 'Total sales', key: 'totalSales', isCurrency: true },
    ],
  };

  static readonly STATUS_TAB_STYLES: Record<
    IndirectTaxFilingStatus,
    IIndirectTaxFilingStatusStyles
  > = {
    [IndirectTaxFilingStatus.ACTIVE]: { text: 'text-fg-brand-primary' },
    [IndirectTaxFilingStatus.FILED]: { text: 'text-text-success' },
    [IndirectTaxFilingStatus.SCHEDULED]: { text: 'text-text-warning' },
    [IndirectTaxFilingStatus.ACCRUING]: { text: 'text-text-tertiary' },
    [IndirectTaxFilingStatus.OVERDUE]: { text: 'text-text-error' },
  };

  static readonly UI_STATUS_TO_API_STATUS: Record<
    IndirectTaxFilingStatus,
    SalesTaxFilingStatusEnum
  > = {
    [IndirectTaxFilingStatus.ACTIVE]: SalesTaxFilingStatusEnum.ACTIVE,
    [IndirectTaxFilingStatus.FILED]: SalesTaxFilingStatusEnum.FILED,
    [IndirectTaxFilingStatus.SCHEDULED]: SalesTaxFilingStatusEnum.SCHEDULED,
    [IndirectTaxFilingStatus.ACCRUING]: SalesTaxFilingStatusEnum.ACCRUING,
    [IndirectTaxFilingStatus.OVERDUE]: SalesTaxFilingStatusEnum.OVERDUE,
  };

  static readonly API_STATUS_TO_UI_STATUS: Record<
    SalesTaxFilingStatusEnum,
    IndirectTaxFilingStatus
  > = {
    [SalesTaxFilingStatusEnum.SCHEDULED]: IndirectTaxFilingStatus.SCHEDULED,
    [SalesTaxFilingStatusEnum.ACCRUING]: IndirectTaxFilingStatus.ACCRUING,
    [SalesTaxFilingStatusEnum.ACTIVE]: IndirectTaxFilingStatus.ACTIVE,
    [SalesTaxFilingStatusEnum.FILED]: IndirectTaxFilingStatus.FILED,
    [SalesTaxFilingStatusEnum.OVERDUE]: IndirectTaxFilingStatus.OVERDUE,
  };

  // String-keyed so we can label backend frequency values that may not yet
  // exist in the frontend's generated Prisma client. Backend is the source of
  // truth for which frequencies get sent to this filter.
  static readonly FREQUENCY_LABELS: Record<string, string> = {
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    QUARTERLY_PREPAY_MONTHLY: 'Quarterly (prepay monthly)',
    SEMI_ANNUALLY: 'Semi-annually',
    ANNUAL_CALENDAR_YEAR: 'Annually',
    ANNUAL_FISCAL_YEAR: 'Annually (fiscal year)',
    MONTHLY_ACCELERATED_PREPAY_EARLY: 'Monthly (accelerated prepay, early)',
    FISCAL_YEAR: 'Fiscal year',
    ANNUAL_RECONCILIATION: 'Annual reconciliation',
    BI_MONTHLY: 'Bi-monthly',
    TRI_ANNUALLY: 'Tri-annually',
    QUARTERLY_FEB: 'Quarterly (Feb start)',
    QUARTERLY_MAR: 'Quarterly (Mar start)',
  };

  // Canonical list of every backend frequency value that can appear on a
  // filing. Kept here because the frontend Prisma client sometimes lags the
  // backend schema on newly added enum variants.
  static readonly FREQUENCY_VALUES: string[] = [
    'MONTHLY',
    'QUARTERLY',
    'QUARTERLY_PREPAY_MONTHLY',
    'SEMI_ANNUALLY',
    'ANNUAL_CALENDAR_YEAR',
    'MONTHLY_ACCELERATED_PREPAY_EARLY',
    'FISCAL_YEAR',
    'ANNUAL_RECONCILIATION',
    'BI_MONTHLY',
    'TRI_ANNUALLY',
    'QUARTERLY_FEB',
    'QUARTERLY_MAR',
  ];

  // Maps service-request task IDs to the client-facing action they trigger on
  // the filing detail page. Absent from this map => no client action for that
  // task (either agent-only, or terminal) and the task bar stays hidden.
  static readonly TASK_ID_TO_CLIENT_ACTION: Record<
    string,
    IndirectTaxFilingClientActionType
  > = {
    REVIEW_TRANSACTIONS: IndirectTaxFilingClientActionType.APPROVE,
  };

  static readonly TASK_DESCRIPTIONS: Record<
    string,
    { client: string; agent: string }
  > = {
    GENERATE_FILING_REPORT: {
      client:
        'Commenda team is reviewing your filing. No action needed from you right now.',
      agent: 'Review the filing data and prepare it for client approval.',
    },
    REVIEW_TRANSACTIONS: {
      client: "Approve the return figures and we'll submit your filings.",
      agent:
        'Waiting for client to review transactions and approve the filing.',
    },
    CONFIRM_FILING: {
      client:
        'Commenda team is submitting the filing. You will be notified once it is confirmed.',
      agent:
        'Submit the filing to the tax authority and record the confirmation number.',
    },
  };

  static readonly TASK_TITLE: Record<
    string,
    { client: string; agent: string }
  > = {
    REVIEW_TRANSACTIONS: {
      client: 'Review and approve return',
      agent: 'Review and approve return.',
    },
  };

  static readonly CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: 'C$',
  };

  // Jurisdiction types that map 1:1 onto a single US state / CA province, so
  // the transactions DESTINATION_STATES filter can be prefilled.
  static readonly STATE_LIKE_JURISDICTION_TYPES: ReadonlySet<IndirectTaxJurisdictionType> =
    new Set([IndirectTaxJurisdictionType.STATE_OR_PROVINCE]);

  // Jurisdiction types that represent a single country, so the transactions
  // DESTINATION_COUNTRIES filter can be prefilled with the filing's country.
  static readonly COUNTRY_LIKE_JURISDICTION_TYPES: ReadonlySet<IndirectTaxJurisdictionType> =
    new Set([IndirectTaxJurisdictionType.COUNTRY]);

  static getStatusBadgeConfig(
    status: IndirectTaxFilingStatus
  ): IIndirectTaxFilingStatusBadgeConfig {
    return IndirectTaxFilingsHelper.STATUS_BADGE_CONFIG[status];
  }

  /**
   * Normalize jurisdiction names (often arrive UPPERCASE from content) to
   * sentence case per word: "NEW SOUTH WALES" -> "New South Wales".
   * Preserves hyphens, apostrophes, and multi-word separators.
   */
  static formatJurisdictionName(name: string | null | undefined): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(
        /(^|[\s\-'])([a-z])/g,
        (_, sep: string, ch: string) => sep + ch.toUpperCase()
      );
  }

  static getStatusTabOrder(): IndirectTaxFilingStatus[] {
    return IndirectTaxFilingsHelper.STATUS_TAB_ORDER;
  }

  static getStatusFilterTabs(
    statusCounts: Partial<Record<SalesTaxFilingStatusEnum, number>> = {}
  ): FilterTab<IndirectTaxFilingStatus | 'ALL'>[] {
    const totalCount = Object.values(statusCounts).reduce<number>(
      (sum, value) => sum + (value ?? 0),
      0
    );

    const allTab: FilterTab<IndirectTaxFilingStatus | 'ALL'> = {
      id: 'ALL',
      label: 'All',
      count: totalCount,
    };

    const statusTabs = IndirectTaxFilingsHelper.STATUS_TAB_ORDER.map(
      (status): FilterTab<IndirectTaxFilingStatus | 'ALL'> => {
        const config = IndirectTaxFilingsHelper.STATUS_BADGE_CONFIG[status];
        const style = IndirectTaxFilingsHelper.STATUS_TAB_STYLES[status];
        const apiStatus = IndirectTaxFilingsHelper.toApiStatus(status);

        return {
          id: status,
          label: config.label,
          count: statusCounts[apiStatus],
          textClassName: style.text,
        };
      }
    );

    return [allTab, ...statusTabs];
  }

  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value)
      .replace('-', '−');
  }

  static getCurrencySymbol(currency: string): string {
    return IndirectTaxFilingsHelper.CURRENCY_SYMBOLS[currency] ?? currency;
  }

  // Older filings and pre-currency deployments retain their established display.
  // New regions have no default: only an explicit reporting currency is used.
  static getFilingCurrency(
    filing: { reportingCurrency?: string | null },
    region: IndirectTaxRegion
  ): string {
    return filing.reportingCurrency || this.getDefaultCurrencyForRegion(region);
  }

  static getDefaultCurrencyForRegion(region: IndirectTaxRegion): string {
    return IndirectTaxRegionHelper.getDefaultCurrencyForRegion(region);
  }

  // Aggregate summary cards are only shown for the United States. EU/UK/Canada
  // are hidden alongside ALL_REGIONS (the latter can't be aggregated at all —
  // cross-currency sums can't be honestly labeled). Dashboard hook and table
  // both consult this predicate so the rule lives in one place.
  static supportsAggregate(region: IndirectTaxRegion): boolean {
    return region === IndirectTaxRegion.UNITED_STATES;
  }

  // Narrow the UI's IndirectTaxRegion to the aggregate API's region union.
  // Returns null for ALL_REGIONS (the API rejects cross-region requests),
  // so callers can short-circuit without an `as` cast.
  static toAggregateRegion(
    region: IndirectTaxRegion
  ): IIndirectTaxFilingV2AggregateRegion | null {
    switch (region) {
      case IndirectTaxRegion.UNITED_STATES:
        return 'UNITED_STATES';
      case IndirectTaxRegion.EUROPEAN_UNION:
        return 'EUROPEAN_UNION';
      case IndirectTaxRegion.UNITED_KINGDOM:
        return 'UNITED_KINGDOM';
      case IndirectTaxRegion.CANADA:
        return 'CANADA';
      case IndirectTaxRegion.ASIA_PACIFIC:
      case IndirectTaxRegion.MIDDLE_EAST:
      case IndirectTaxRegion.LATIN_AMERICA:
      case IndirectTaxRegion.UNSUPPORTED:
      case IndirectTaxRegion.ALL_REGIONS:
        return null;
      default: {
        const unhandledRegion: never = region;
        throw new Error(`Unhandled indirect tax region: ${unhandledRegion}`);
      }
    }
  }

  // Build the dashboard summary cards from an aggregate response. Returns
  // undefined when the region can't be aggregated (ALL_REGIONS) so the table
  // omits the cards entirely; returns { items: [] } during first-load before
  // the aggregate resolves so the layout doesn't pop in. Labels are returned
  // ready to render — currency-bucket labels already carry the symbol
  // suffix, so callers don't patch them post-call.
  static computeAggregateSummaryItems(
    region: IndirectTaxRegion,
    aggregate: IIndirectTaxFilingV2AggregateResponse | null
  ): { items: { label: string; value: string }[] } | undefined {
    if (!IndirectTaxFilingsHelper.supportsAggregate(region)) return undefined;
    if (!aggregate) return { items: [] };

    const configs = IndirectTaxFilingsHelper.SUMMARY_CONFIGS[region];
    const currencySymbol = IndirectTaxFilingsHelper.getCurrencySymbol(
      aggregate.currency
    );

    return {
      items: configs.map((cfg) => {
        const raw = IndirectTaxFilingsHelper.getAggregateBucketValue(
          aggregate.totals,
          cfg.key
        );
        const value =
          raw === undefined
            ? '—'
            : IndirectTaxFilingsHelper.formatCurrency(raw);
        const label = cfg.isCurrency
          ? `${cfg.label} (${currencySymbol})`
          : cfg.label;
        return { label, value };
      }),
    };
  }

  // Exhaustive lookup from the SUMMARY_CONFIGS key space (typed against
  // IIndirectTaxFiling) into the aggregate totals shape. Every key the
  // configs reference is also a field on IIndirectTaxFilingV2AggregateTotals;
  // anything else returns undefined and the card renders as '—'.
  private static getAggregateBucketValue(
    totals: IIndirectTaxFilingV2AggregateTotals,
    key: keyof IIndirectTaxFiling
  ): number | undefined {
    switch (key) {
      case 'taxableSales':
        return totals.taxableSales;
      case 'nonTaxableSales':
        return totals.nonTaxableSales;
      case 'exemptSales':
        return totals.exemptSales;
      case 'taxDue':
        return totals.taxDue;
      case 'totalSales':
        return totals.totalSales;
      case 'taxablePurchases':
        return totals.taxablePurchases;
      case 'intraCommunitySales':
        return totals.intraCommunitySales;
      case 'intraCommunityPurchases':
        return totals.intraCommunityPurchases;
      case 'vatPosition':
        return totals.vatPosition;
      default:
        return undefined;
    }
  }

  static getPendingTasks(
    tasks: Readonly<IIndirectTaxFilingTask[]>
  ): IIndirectTaxFilingTask[] {
    return tasks.filter(
      (task) => task.status === IndirectTaxFilingTaskStatus.PENDING
    );
  }

  static canApproveFiling(
    serviceRequest: IIndirectTaxFilingV2ServiceRequestAssociation | null
  ): boolean {
    return IndirectTaxFilingsHelper.deriveFilingTasks(serviceRequest).some(
      (task) => task.actionType === IndirectTaxFilingClientActionType.APPROVE
    );
  }

  static canReviewFiling(
    serviceRequest:
      | IIndirectTaxFilingV2ServiceRequestAssociation
      | null
      | undefined
  ): boolean {
    if (!serviceRequest || serviceRequest.status === 'COMPLETED') return false;

    const currentTask = (serviceRequest.taskGroups ?? [])
      .flatMap((tg) => tg.tasks ?? [])
      .sort(
        (a, b) =>
          (a.legacyServiceRequestTaskOrderNumber ?? 0) -
          (b.legacyServiceRequestTaskOrderNumber ?? 0)
      )
      .find((t) => t.status !== 'COMPLETED');

    return (
      currentTask?.legacyServiceRequestTaskTaskId ===
        'GENERATE_FILING_REPORT' && currentTask?.pendingOn === 'AGENT'
    );
  }

  static formatFiledOnDate(dateString: string): string {
    const date = new Date(dateString);
    // Date-only value parsed as UTC midnight; format in UTC so users behind UTC
    // don't see the date slip back by a day (PLAT-2537).
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  static toApiStatus(
    status: IndirectTaxFilingStatus
  ): SalesTaxFilingStatusEnum {
    return IndirectTaxFilingsHelper.UI_STATUS_TO_API_STATUS[status];
  }

  static mapApiStatus(
    status: SalesTaxFilingStatusEnum
  ): IndirectTaxFilingStatus {
    return (
      IndirectTaxFilingsHelper.API_STATUS_TO_UI_STATUS[status] ??
      IndirectTaxFilingStatus.ACTIVE
    );
  }

  // Trim an ISO timestamp to its YYYY-MM-DD prefix. Filing reporting periods
  // arrive as full ISO strings but the transactions date filter encodes
  // `custom:<from>,<to>` with date-only parts.
  static toDateOnly(value: string): string {
    return value.slice(0, 10);
  }

  // Build the transactions dashboard initialFilters for a filing "View
  // transactions" modal. Pure domain logic: translates the filing's
  // reporting period, jurisdiction type and geography into the
  // TIndirectTaxTransactionTableFilters shape the dashboard hook accepts.
  static buildTransactionFiltersForFiling(
    filing: IIndirectTaxFilingDetail,
    region: IndirectTaxRegion
  ): Record<string, string | string[]> {
    const filters: Record<string, string | string[]> = {};

    if (filing.reportingPeriodStartDate && filing.reportingPeriodEndDate) {
      filters[TIndirectTaxTransactionTableFilters.REPORTING_DATE] =
        `custom:${IndirectTaxFilingsHelper.toDateOnly(filing.reportingPeriodStartDate)},${IndirectTaxFilingsHelper.toDateOnly(filing.reportingPeriodEndDate)}`;
    }

    const jurisdictionType = filing.jurisdictionType;

    if (
      IndirectTaxFilingsHelper.STATE_LIKE_JURISDICTION_TYPES.has(
        jurisdictionType
      )
    ) {
      const stateFilterCode = IndirectTaxFilingsHelper.getStateFilterCode(
        filing.countryCode,
        filing.jurisdiction
      );
      if (stateFilterCode) {
        filters[TIndirectTaxTransactionTableFilters.DESTINATION_STATES] = [
          stateFilterCode,
        ];
      }
    } else if (jurisdictionType === IndirectTaxJurisdictionType.TRADEBLOC) {
      const countries = IndirectTaxRegionHelper.getCountriesForRegion(region);
      if (countries && countries.length > 0) {
        filters[TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES] =
          countries;
      }
    } else if (
      IndirectTaxFilingsHelper.COUNTRY_LIKE_JURISDICTION_TYPES.has(
        jurisdictionType
      ) &&
      filing.countryCode
    ) {
      filters[TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES] = [
        filing.countryCode,
      ];
    }

    filters[TIndirectTaxTransactionTableFilters.STATUS] = [
      TIndirectTaxTransactionStatus.LOCKED,
      TIndirectTaxTransactionStatus.UNCOMMITTED,
      TIndirectTaxTransactionStatus.COMMITTED,
    ];

    return filters;
  }

  /**
   * Return the `${country}_${stateIso}` form the transactions
   * `DESTINATION_STATES` filter expects for the given country + state/province
   * name. Uses the same `country-state-city` source as the filter options
   * (see IndirectTaxTransactionsTable) so the preset value always matches an
   * option in the dropdown. Returns null when no subdivision matches.
   */
  static getStateFilterCode(
    countryCode: string,
    stateName: string
  ): string | null {
    if (!countryCode || !stateName) return null;
    if (countryCode !== 'US' && countryCode !== 'CA') return null;
    const match = IndirectTaxRegionHelper.findStateByName(
      countryCode,
      stateName
    );
    return match ? `${countryCode}_${match.isoCode}` : null;
  }

  static getFrequencyFilterOptions(): { label: string; value: string }[] {
    return IndirectTaxFilingsHelper.FREQUENCY_VALUES.map((value) => ({
      label: IndirectTaxFilingsHelper.FREQUENCY_LABELS[value] ?? value,
      value,
    }));
  }

  static getFilingFrequencyName(frequency: string): string {
    return IndirectTaxFilingsHelper.FREQUENCY_LABELS[frequency] ?? frequency;
  }

  static regionSupportsJurisdictionFilter(region: IndirectTaxRegion): boolean {
    // UK is a single-jurisdiction region; no filter value would ever narrow it.
    return region !== IndirectTaxRegion.UNITED_KINGDOM;
  }

  static formatFilingPeriod(startDate: string, frequency: string): string {
    if (!startDate) return '';

    const start = new Date(startDate);
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const freqLower = frequency?.toLowerCase() ?? '';

    // Reporting period dates are date-only values (e.g. "2026-06-01") that JS
    // parses as UTC midnight. Read them back in UTC so users behind UTC don't
    // see the period slip to the previous month (PLAT-2537).
    if (freqLower.includes('annual')) {
      return `${start.getUTCFullYear()}`;
    }

    if (freqLower.includes('quarter')) {
      const quarter = Math.ceil((start.getUTCMonth() + 1) / 3);
      return `Q${quarter} ${start.getUTCFullYear()}`;
    }

    if (freqLower.includes('semi')) {
      const half = start.getUTCMonth() < 6 ? 'H1' : 'H2';
      return `${half} ${start.getUTCFullYear()}`;
    }

    return `${monthNames[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
  }

  static formatDueDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Due dates are date-only values parsed as UTC midnight; format in UTC so
    // users behind UTC don't see the date slip back by a day (PLAT-2537).
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  // Filing detail task bar shows ONLY the client's current actionable step, so
  // the bar hides whenever it's the agent's turn, the filing is already
  // submitted, or the service request hasn't started. The returned task carries
  // an `actionType` that drives which modal opens from the task table.
  static deriveFilingTasks(
    serviceRequest:
      | IIndirectTaxFilingV2ServiceRequestAssociation
      | null
      | undefined
  ): IIndirectTaxFilingTask[] {
    if (!serviceRequest || serviceRequest.status === 'COMPLETED') return [];

    const flat = (serviceRequest.taskGroups ?? [])
      .flatMap((tg) => tg.tasks ?? [])
      .sort(
        (a, b) =>
          (a.legacyServiceRequestTaskOrderNumber ?? 0) -
          (b.legacyServiceRequestTaskOrderNumber ?? 0)
      );

    const currentTask = flat.find((t) => t.status !== 'COMPLETED');
    if (!currentTask || currentTask.pendingOn !== 'CLIENT') return [];

    const actionType =
      IndirectTaxFilingsHelper.TASK_ID_TO_CLIENT_ACTION[
        currentTask.legacyServiceRequestTaskTaskId
      ];
    if (!actionType) return [];

    const description =
      IndirectTaxFilingsHelper.TASK_DESCRIPTIONS[
        currentTask.legacyServiceRequestTaskTaskId
      ]?.client ?? currentTask.title;

    const title =
      IndirectTaxFilingsHelper.TASK_TITLE[
        currentTask.legacyServiceRequestTaskTaskId
      ]?.client ?? currentTask.title;

    return [
      {
        id: currentTask.id,
        title,
        description,
        status: IndirectTaxFilingTaskStatus.PENDING,
        actionType,
      },
    ];
  }

  // VAT filings hide the return summary until the agent first submits the
  // filing report (i.e. GENERATE_FILING_REPORT has been completed at least
  // once). Detection is intentionally monotonic: REVIEW_TRANSACTIONS only
  // activates once GENERATE_FILING_REPORT completes, and once activated it
  // never reverts to TO_DO/BACKLOG — so a later client "report issue" (which
  // reopens both tasks as IN_PROGRESS) keeps the summary visible. US/Canada
  // never gate on this — callers only consult it for VAT filings.
  static hasGeneratedFilingReportAtLeastOnce(
    serviceRequest:
      | IIndirectTaxFilingV2ServiceRequestAssociation
      | null
      | undefined
  ): boolean {
    if (!serviceRequest) return false;

    const tasks = (serviceRequest.taskGroups ?? []).flatMap(
      (tg) => tg.tasks ?? []
    );
    const generateReport = tasks.find(
      (t) => t.legacyServiceRequestTaskTaskId === 'GENERATE_FILING_REPORT'
    );
    if (generateReport?.status === 'COMPLETED') return true;

    const reviewTransactions = tasks.find(
      (t) => t.legacyServiceRequestTaskTaskId === 'REVIEW_TRANSACTIONS'
    );
    const dormantStatuses = ['TO_DO', 'BACKLOG'];
    return Boolean(
      reviewTransactions && !dormantStatuses.includes(reviewTransactions.status)
    );
  }

  // True when the service request's current (first incomplete) step is
  // GENERATE_FILING_REPORT — i.e. the agent is still reviewing and there is no
  // client action yet. Mirrors deriveFilingTasks' "first non-completed task"
  // logic.
  static isOnGenerateFilingReportStep(
    serviceRequest:
      | IIndirectTaxFilingV2ServiceRequestAssociation
      | null
      | undefined
  ): boolean {
    if (!serviceRequest || serviceRequest.status === 'COMPLETED') return false;

    const currentTask = (serviceRequest.taskGroups ?? [])
      .flatMap((tg) => tg.tasks ?? [])
      .sort(
        (a, b) =>
          (a.legacyServiceRequestTaskOrderNumber ?? 0) -
          (b.legacyServiceRequestTaskOrderNumber ?? 0)
      )
      .find((t) => t.status !== 'COMPLETED');

    return (
      currentTask?.legacyServiceRequestTaskTaskId === 'GENERATE_FILING_REPORT'
    );
  }

  // Build the detail-page filing object. `amountDue` is sourced from the
  // return summary so the top metric header and the bold "Amount due" row at
  // the bottom of the Return summary are guaranteed to match — single source
  // of truth. Calendar-only filings have no financial summary.
  static fromApiFilingDetail(
    filing: IIndirectTaxFilingV2WithAssociations,
    returnSummary: IIndirectTaxFilingReturnSummary | null
  ): IIndirectTaxFilingDetail {
    const baseFiling = IndirectTaxFilingListRowHelper.toListRow(filing);

    return {
      ...baseFiling,
      tasks: IndirectTaxFilingsHelper.deriveFilingTasks(filing.serviceRequest),
      serviceRequest: filing.serviceRequest ?? null,
      remittanceAccount: null,
      filingConfirmationDocuments: [],
      relatedDocuments: [],
      amountDue: returnSummary?.amountDue ?? null,
      transactionCount: filing.filingTransactionsCount ?? 0,
      filedOn: filing.filedAt
        ? IndirectTaxFilingsHelper.formatFiledOnDate(filing.filedAt)
        : '',
      filedByAgentName: filing.filedByAgent?.name ?? null,
      entityName: '',
      filingLockedAt: filing.filingLockedAt ?? null,
      entityCountryCode: baseFiling.countryCode,
      reportingPeriodStartDate: filing.reportingPeriodStartDate,
      reportingPeriodEndDate: filing.reportingPeriodEndDate,
      // Backend emits the string form; coerce to the UI enum. Unknown values
      // fall back to COUNTRY (the most generic bucket) rather than throwing,
      // matching how other UI helpers tolerate new backend variants.
      jurisdictionType: IndirectTaxFilingsHelper.toJurisdictionType(
        filing.jurisdictionType
      ),
      jurisdictionId: filing.jurisdictionId,
      registrationTaxTypeId: filing.registrationTaxTypeId,
    };
  }

  static toJurisdictionType(value: string): IndirectTaxJurisdictionType {
    const upper = value?.toUpperCase();
    if (upper in IndirectTaxJurisdictionType) {
      return upper as IndirectTaxJurisdictionType;
    }
    return IndirectTaxJurisdictionType.COUNTRY;
  }

  static toFilingDocument(
    doc: IIndirectTaxFilingV2DocumentWithFile
  ): IIndirectTaxFilingDocument {
    // FileBasic requires documentType as LegacyDocumentType; the V2 filing
    // document-type enum is not mapped 1:1. FilingDocument only renders
    // fileName + signedUrl + createdAt, so OTHER is a safe neutral fallback.
    return {
      fileName: doc.fileName,
      fileUrl: doc.signedUrl,
      signedUrl: doc.signedUrl,
      documentType: LegacyDocumentType.Other,
      createdAt: doc.createdAtEpochMicros,
    };
  }
}
